/**
 * MarkerOverlay - Lets the user draw a freehand stroke on the current
 * refinement image to create a "reference mark" the prompt can refer to.
 *
 * Single toolbar button (top-right of the image) toggles marker mode.
 * While the mode is active a canvas overlays the image; as the user
 * hovers, we sample a backend-supplied brightness grid under the cursor
 * and tint the marker icon highlighter-yellow (over dark image regions)
 * or magenta (over light regions) — whichever will be visible on the
 * spot they're about to click. On pointerdown we lock that color for
 * the whole stroke.
 *
 * Pointer-down + drag + pointer-up draws one stroke on the canvas. On
 * pointer-up we export ONLY the transparent stroke overlay as a PNG at
 * the displayed (CSS-pixel) canvas size and pass it to `onMarkCreated`;
 * the backend resizes the overlay to the session image's native
 * resolution and alpha-composites it server-side. We deliberately do
 * NOT draw the cross-origin <img> into a canvas here — that would taint
 * the canvas under typical GCS-without-CORS deployments and break the
 * feature. The brightness grid arrives over the session-response wire
 * for exactly that reason: precomputed server-side so the client never
 * needs CORS-enabled pixel access.
 *
 * The toolbar is rendered absolutely so the parent positions it within
 * the preview's containing element. The drawing canvas only mounts
 * when marker mode is active — when idle the parent's pointer events
 * (click to select image, etc.) work normally.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { MarkShade } from '@/types/creativeStudio';
import { MARKER_YELLOW_HEX, MARKER_MAGENTA_HEX } from '@/config/creativeStudio';
import '@/styles/tooltip.css';

/**
 * Inline highlighter glyph. heroicons doesn't ship one, and PaintBrushIcon
 * reads as "painter's brush" rather than the highlighter affordance we
 * want here. Shape adapted from Lucide's `highlighter` (MIT). Uses
 * currentColor so the parent's `style.color` (auto-picked yellow/magenta
 * during marker mode, neutral gray when idle) drives the visible tint.
 */
function HighlighterIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m9 11-6 6v3h9l3-3" />
      <path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4" />
    </svg>
  );
}

/**
 * Bounding box of the stroke, expressed as fractions (0..1) of the displayed
 * image's width/height. Sent alongside the PNG so the backend can derive a
 * qualitative position hint (e.g., "upper-right") and coverage bucket
 * (e.g., "small") without needing pixel coordinates.
 */
export interface MarkBoundingBox {
  x_frac: number;
  y_frac: number;
  w_frac: number;
  h_frac: number;
}

interface MarkerOverlayProps {
  imageEl: HTMLImageElement | null;
  /** Square row-major luminance grid for the current image, values in
   *  [0, 1]. Resolution set by the backend (currently 64×64) and read
   *  dynamically via `.length`. Null falls back to yellow as a single
   *  safe default. */
  brightnessGrid?: number[][] | null;
  disabled?: boolean;
  isUploading?: boolean;
  /** When set, the button renders disabled and its tooltip/title shows
   *  this string. Used to surface session-level caps (e.g. "3 marks
   *  already attached") without baking the cap rules into this component. */
  unavailableReason?: string | null;
  onMarkCreated: (
    blob: Blob,
    shade: MarkShade,
    bbox: MarkBoundingBox | null
  ) => Promise<void> | void;
}

// Highlighter colors — imported from config/creativeStudio so the
// values stay in lockstep with RefinementPanel's mark-badge swatch
// and the backend prompt's hex references.
const YELLOW_COLOR = MARKER_YELLOW_HEX;
const MAGENTA_COLOR = MARKER_MAGENTA_HEX;

// Luminance threshold for the marker color decision. Cells brighter
// than this take magenta; darker take yellow. No hysteresis — at 64×64
// the per-cell averages are already smoothed, so the only practical
// effect of a buffer was that mid-range cells could stick at the
// previous (lower-contrast) color. Add a narrow band back if real-use
// shows flicker.
const LUMINANCE_THRESHOLD = 0.5;

// Stroke width as a fraction of the smaller image dimension. Keeps the
// mark visually proportional regardless of resolution — a 1:1 1K image
// gets a ~10 px stroke; a 4K image gets ~40 px.
const STROKE_WIDTH_FRACTION = 0.01;

/**
 * Render the full stroke as a single tapered polygon — thick at the start
 * ("brush pressing down on the canvas"), tapering smoothly to a thin point
 * at the end ("brush easing off"). The shape itself communicates pressure;
 * we deliberately do NOT vary opacity by speed.
 *
 * Construction (adapted from the `varLineRounded` technique used for
 * variable-width canvas lines):
 *
 *  1. Compute per-point unit normals (perpendicular to the local tangent).
 *  2. Compute per-point widths from a taper curve over t = i/(n-1).
 *  3. Walk forward along the "+normal" side from point 0 to point n-1.
 *  4. Draw a semicircle end cap at point n-1 (radius = local width / 2),
 *     bulging forward along the tangent.
 *  5. Walk back along the "-normal" side from point n-1 to point 0.
 *  6. Draw a semicircle start cap at point 0, bulging backward.
 *  7. Close and fill once.
 *
 * Drawing the whole stroke as ONE filled polygon (instead of per-segment
 * strokes) avoids the visible seams you'd otherwise get where adjacent
 * segments meet — important when the width changes between points.
 *
 * Caller redraws the full polygon on every pointermove (canvas cleared
 * first), because adding a new point shifts every other point's `t`
 * value, so every width along the stroke needs to be recomputed.
 */
function drawTaperedStroke(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  color: string,
  baseWidth: number,
): void {
  if (points.length === 0) return;
  ctx.fillStyle = color;

  // Single point = a filled dot. A "tap without drag" still leaves a mark.
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, baseWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  // All-coincident-points short-circuit: if every pointer sample lands in
  // the same sub-pixel cell, `normalAt` collapses to (0, 0) and the polygon
  // walk produces a zero-area shape. The end/start arcs happen to render a
  // full disk today because both sweep π radians counter-clockwise from
  // overlapping centers, but that's a fragile accident — a future arc-
  // direction tweak would silently erase the mark. Treat any stroke with
  // <0.5px extent in both dimensions as the dot case.
  let minX = points[0].x;
  let maxX = points[0].x;
  let minY = points[0].y;
  let maxY = points[0].y;
  for (let i = 1; i < points.length; i++) {
    if (points[i].x < minX) minX = points[i].x;
    if (points[i].x > maxX) maxX = points[i].x;
    if (points[i].y < minY) minY = points[i].y;
    if (points[i].y > maxY) maxY = points[i].y;
  }
  if (maxX - minX < 0.5 && maxY - minY < 0.5) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, baseWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  const n = points.length;

  // Taper curve: width(t) = baseWidth * (TAIL_FACTOR + (1 - TAIL_FACTOR) * (1-t)^TAPER_POWER)
  // - TAIL_FACTOR sets the final width as a fraction of base width.
  //   Lower → finer point at the end. 0.5 = end is half the start's width.
  // - TAPER_POWER shapes the easing. >1 holds width near the start and
  //   accelerates the taper toward the end (matches "press down, then
  //   lift off"). =1 would give a straight linear taper.
  const TAIL_FACTOR = 0.5;
  const TAPER_POWER = 1.2;
  const widthAt = (i: number): number => {
    const t = i / (n - 1);
    return baseWidth * (TAIL_FACTOR + (1 - TAIL_FACTOR) * Math.pow(1 - t, TAPER_POWER));
  };

  // Per-point unit normal, perpendicular to the local tangent. For endpoints
  // the tangent uses the single neighbor; for interior points it uses the
  // chord (i-1)→(i+1) so the normal varies smoothly along curves.
  const normalAt = (i: number): { nx: number; ny: number } => {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(n - 1, i + 1)];
    const tx = next.x - prev.x;
    const ty = next.y - prev.y;
    const len = Math.hypot(tx, ty) || 1;
    return { nx: -ty / len, ny: tx / len };
  };

  ctx.beginPath();

  // Side A = point + normal * (w/2). Walk forward from start to end.
  const n0 = normalAt(0);
  const w0 = widthAt(0) / 2;
  ctx.moveTo(points[0].x + n0.nx * w0, points[0].y + n0.ny * w0);
  for (let i = 1; i < n; i++) {
    const ni = normalAt(i);
    const wi = widthAt(i) / 2;
    ctx.lineTo(points[i].x + ni.nx * wi, points[i].y + ni.ny * wi);
  }

  // End cap: arc from side A of last point → side B of last point, bulging
  // FORWARD (away from the previous segment). In canvas, increasing angle
  // is clockwise visually (y-down), so the "forward" bulge is the CCW sweep
  // (counterclockwise=true) — it passes through the tangent direction.
  const nN = normalAt(n - 1);
  const wN = widthAt(n - 1) / 2;
  const endSideA = Math.atan2(nN.ny, nN.nx);
  const endSideB = endSideA + Math.PI;
  ctx.arc(points[n - 1].x, points[n - 1].y, wN, endSideA, endSideB, true);

  // Side B = point - normal * (w/2). Walk back from end to start.
  for (let i = n - 2; i >= 0; i--) {
    const ni = normalAt(i);
    const wi = widthAt(i) / 2;
    ctx.lineTo(points[i].x - ni.nx * wi, points[i].y - ni.ny * wi);
  }

  // Start cap: arc from side B of point 0 → side A of point 0, bulging
  // BACKWARD. Same CCW sweep direction as the end cap, just at the other
  // endpoint of the path.
  const startSideA = Math.atan2(n0.ny, n0.nx);
  const startSideB = startSideA + Math.PI;
  ctx.arc(points[0].x, points[0].y, w0, startSideB, startSideA, true);

  ctx.closePath();
  ctx.fill();
}

export function MarkerOverlay({
  imageEl,
  brightnessGrid,
  disabled,
  isUploading,
  unavailableReason,
  onMarkCreated,
}: MarkerOverlayProps) {
  const [markerModeActive, setMarkerModeActive] = useState(false);
  // Preview color reflects what the cursor is currently over. Mirrored
  // to `lastShadeRef` so the last shade decision survives re-renders
  // without re-binding handlers each time the shade flips, and so
  // shadeAtCursor has a stable fallback when the brightness grid or
  // geometry is briefly unavailable.
  const [previewShade, setPreviewShade] = useState<MarkShade>('yellow');
  const lastShadeRef = useRef<MarkShade>('yellow');
  // Color locked at pointerdown — used for the entire stroke regardless
  // of where the cursor wanders during the drag. Set on pointerdown,
  // cleared on pointerup.
  const lockedShadeRef = useRef<MarkShade | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  // Track the size of the image in CSS pixels so the canvas can stay aligned
  // even when the image is responsive (max-h:500px scales the rendered size).
  const [renderedRect, setRenderedRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  // Width of the canvas overlay's drawing context, used by stroke math.
  // We scale strokes by the ratio of natural-to-rendered so they map cleanly
  // onto the final natural-resolution composite.
  const naturalDims = useRef<{ width: number; height: number } | null>(null);
  // Running bounding box of the in-flight stroke, in CSS pixels relative to
  // the overlay canvas. Normalized to fractions on pointerup and shipped
  // alongside the PNG so the backend can derive a qualitative position
  // hint ("upper-right", "small") for the refinement prompt.
  const strokeBoundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  // All pointer samples for the in-flight stroke, in canvas-local CSS px.
  // We need every sample (not just the latest) because each pointermove
  // re-renders the brush from scratch — the multi-layer alpha effect can't
  // be drawn incrementally without darkening joins.
  const pointsRef = useRef<{ x: number; y: number }[]>([]);
  // Stroke line width in canvas-local CSS px. Computed once at pointerdown
  // from natural/rendered dims and reused across all pointermove redraws so
  // the bristle offsets stay consistent over the life of one stroke.
  const strokeWidthRef = useRef<number>(0);

  // Recompute overlay geometry whenever the image's layout changes — both
  // initial load and window/container resize. Using ResizeObserver on the
  // image element catches all the cases the manual `useEffect([imageEl])`
  // would miss (e.g., the parent flex/grid reflowing).
  useEffect(() => {
    if (!imageEl) {
      setRenderedRect(null);
      naturalDims.current = null;
      return;
    }
    const update = () => {
      const containerEl = imageEl.parentElement;
      if (!containerEl) return;
      const imgRect = imageEl.getBoundingClientRect();
      const parentRect = containerEl.getBoundingClientRect();
      const naturalW = imageEl.naturalWidth || imgRect.width;
      const naturalH = imageEl.naturalHeight || imgRect.height;
      naturalDims.current = { width: naturalW, height: naturalH };

      // The <img> uses `object-contain` with `max-h-[70vh]`, so its border
      // box can be larger than the visible image content (pillarbox left/
      // right when max-h clamps a square/portrait, letterbox top/bottom
      // when a landscape image is fit into a taller box). Sizing the canvas
      // to imgRect would let pointer coords map onto box pixels that fall
      // OUTSIDE the actual image — and because the backend resizes the
      // overlay PNG to the natural image dims, a non-matching aspect ratio
      // squashes/stretches the stroke, displacing the mark from where the
      // user drew it. Mirror object-contain here so the canvas covers
      // exactly the visible image content and the resize is uniform.
      const boxAspect = imgRect.width / imgRect.height;
      const naturalAspect = naturalW / naturalH;
      let visibleW = imgRect.width;
      let visibleH = imgRect.height;
      let offsetX = 0;
      let offsetY = 0;
      if (naturalAspect > boxAspect) {
        // Image wider than box — fits to box width, letterbox top/bottom.
        visibleH = imgRect.width / naturalAspect;
        offsetY = (imgRect.height - visibleH) / 2;
      } else if (naturalAspect < boxAspect) {
        // Image taller than box — fits to box height, pillarbox sides.
        visibleW = imgRect.height * naturalAspect;
        offsetX = (imgRect.width - visibleW) / 2;
      }

      setRenderedRect({
        left: imgRect.left - parentRect.left + offsetX,
        top: imgRect.top - parentRect.top + offsetY,
        width: visibleW,
        height: visibleH,
      });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(imageEl);
    if (imageEl.parentElement) ro.observe(imageEl.parentElement);
    window.addEventListener('resize', update);
    // The image may not have loaded yet when this effect runs. Hook into
    // `load` so we recompute once naturalWidth/Height is meaningful.
    imageEl.addEventListener('load', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      imageEl.removeEventListener('load', update);
    };
  }, [imageEl]);

  // Cancel on Escape — gives the user a quick out if they activate marker
  // mode by mistake or want to abort mid-drag.
  useEffect(() => {
    if (!markerModeActive) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        isDrawingRef.current = false;
        strokeBoundsRef.current = null;
        pointsRef.current = [];
        lockedShadeRef.current = null;
        const ctx = canvasRef.current?.getContext('2d');
        if (ctx && canvasRef.current) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
        setMarkerModeActive(false);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [markerModeActive]);

  // Pick a marker shade for a cursor position in canvas-local CSS pixels.
  // Looks up the brightness-grid cell under the cursor and flips at a
  // single LUMINANCE_THRESHOLD — magenta for bright cells, yellow for
  // dark. `lastShadeRef` only matters when the grid is unavailable
  // (preserves the last chosen color rather than snapping to a default
  // mid-gesture).
  const shadeAtCursor = useCallback((x: number, y: number): MarkShade => {
    const prev = lastShadeRef.current;
    if (
      !brightnessGrid ||
      brightnessGrid.length === 0 ||
      !renderedRect ||
      renderedRect.width <= 0 ||
      renderedRect.height <= 0
    ) {
      return prev;
    }
    const cells = brightnessGrid.length;
    const col = Math.min(cells - 1, Math.max(0, Math.floor((x / renderedRect.width) * cells)));
    const row = Math.min(cells - 1, Math.max(0, Math.floor((y / renderedRect.height) * cells)));
    const cellRow = brightnessGrid[row];
    if (!cellRow || cellRow.length === 0) return prev;
    const lum = cellRow[Math.min(col, cellRow.length - 1)] ?? 0.5;
    const next: MarkShade = lum > LUMINANCE_THRESHOLD ? 'magenta' : 'yellow';
    lastShadeRef.current = next;
    return next;
  }, [brightnessGrid, renderedRect]);

  const composeAndUpload = useCallback(async () => {
    const overlay = canvasRef.current;
    const lockedShade = lockedShadeRef.current;
    if (!overlay || !lockedShade) return;

    // Derive the stroke bbox in normalized image fractions before we clear
    // the bounds. `renderedRect.width/height` is the canvas CSS size, which
    // equals the displayed image size, so dividing canvas-space coords by
    // those gives 0..1 fractions that are resolution-independent.
    let bbox: MarkBoundingBox | null = null;
    const bounds = strokeBoundsRef.current;
    if (
      bounds &&
      renderedRect &&
      renderedRect.width > 0 &&
      renderedRect.height > 0
    ) {
      const clamp = (v: number) => Math.max(0, Math.min(1, v));
      const x_frac = clamp(bounds.minX / renderedRect.width);
      const y_frac = clamp(bounds.minY / renderedRect.height);
      // Pointer-capture lets the drag escape the canvas, so raw
      // (maxX - minX) can exceed renderedRect.width. Clamp width/height
      // to (1 - origin) so x + w never exceeds 1 — the backend's hint
      // derivation would otherwise re-clamp `cx` and produce the right
      // bucket *for the wrong reason*, which is fragile.
      const w_raw = (bounds.maxX - bounds.minX) / renderedRect.width;
      const h_raw = (bounds.maxY - bounds.minY) / renderedRect.height;
      const w_frac = Math.max(0, Math.min(1 - x_frac, w_raw));
      const h_frac = Math.max(0, Math.min(1 - y_frac, h_raw));
      bbox = { x_frac, y_frac, w_frac, h_frac };
    }
    strokeBoundsRef.current = null;
    pointsRef.current = [];

    // We deliberately export ONLY the overlay (stroke on transparent
    // background) and let the backend composite it onto the session's
    // current image with PIL. Drawing the cross-origin <img> into a
    // canvas here would taint it under the typical GCS CORS-not-configured
    // case and break the feature entirely; this path never touches the
    // image element so the canvas can't taint.
    let blob: Blob | null = null;
    try {
      blob = await new Promise((resolve) =>
        overlay.toBlob((b) => resolve(b), 'image/png')
      );
    } catch (err) {
      console.error('toBlob threw on overlay canvas:', err);
      toast.error('Failed to export the reference mark.');
      lockedShadeRef.current = null;
      setMarkerModeActive(false);
      return;
    }
    if (!blob) {
      console.error('toBlob returned null on overlay canvas');
      toast.error('Failed to export the reference mark.');
      lockedShadeRef.current = null;
      setMarkerModeActive(false);
      return;
    }

    try {
      await onMarkCreated(blob, lockedShade, bbox);
    } finally {
      // Reset overlay regardless of success — the parent will refetch the
      // session and the new mark will appear in the references list.
      const octx = overlay.getContext('2d');
      if (octx) octx.clearRect(0, 0, overlay.width, overlay.height);
      lockedShadeRef.current = null;
      setMarkerModeActive(false);
    }
  }, [onMarkCreated, renderedRect]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const overlay = canvasRef.current;
      if (!overlay || !markerModeActive || !renderedRect) return;
      const ctx = overlay.getContext('2d');
      if (!ctx) return;
      overlay.setPointerCapture(e.pointerId);
      isDrawingRef.current = true;
      const rect = overlay.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      pointsRef.current = [{ x, y }];
      strokeBoundsRef.current = { minX: x, minY: y, maxX: x, maxY: y };

      // Scale stroke width to natural resolution so the composite renders
      // at the intended visual weight even when the displayed image is
      // smaller than the natural image. Locked once per stroke.
      const naturalMin = naturalDims.current
        ? Math.min(naturalDims.current.width, naturalDims.current.height)
        : Math.min(renderedRect.width, renderedRect.height);
      const naturalStroke = Math.max(4, naturalMin * STROKE_WIDTH_FRACTION);
      const renderedScale = renderedRect.width / (naturalDims.current?.width || renderedRect.width);
      strokeWidthRef.current = naturalStroke * renderedScale;

      // Lock the marker color for this stroke based on what's under the
      // cursor at pointerdown. Honors hysteresis the same way hover did,
      // so the visible preview color and the locked color always agree.
      const shade = shadeAtCursor(x, y);
      lockedShadeRef.current = shade;
      setPreviewShade((cur) => (cur === shade ? cur : shade));

      // Initial render so a pure tap (no drag) still leaves a visible dot.
      const color = shade === 'yellow' ? YELLOW_COLOR : MAGENTA_COLOR;
      drawTaperedStroke(ctx, pointsRef.current, color, strokeWidthRef.current);
    },
    [markerModeActive, renderedRect, shadeAtCursor]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const overlay = canvasRef.current;
      if (!overlay || !markerModeActive) return;
      const ctx = overlay.getContext('2d');
      if (!ctx) return;
      const rect = overlay.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (!isDrawingRef.current) {
        // Hover: sample the brightness grid and update the preview color
        // shown on the toolbar button. No draw happens until pointerdown.
        const next = shadeAtCursor(x, y);
        setPreviewShade((cur) => (cur === next ? cur : next));
        return;
      }

      pointsRef.current.push({ x, y });
      const bounds = strokeBoundsRef.current;
      if (bounds) {
        if (x < bounds.minX) bounds.minX = x;
        if (y < bounds.minY) bounds.minY = y;
        if (x > bounds.maxX) bounds.maxX = x;
        if (y > bounds.maxY) bounds.maxY = y;
      }
      // The taper depends on each point's position in the full stroke
      // (t = i / (n-1)), so adding a point shifts every other point's
      // width. We have to clear and redraw the whole polygon — there's no
      // valid incremental path. Cheap in practice: one fill() over a
      // single polygon per move.
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      const shade = lockedShadeRef.current ?? previewShade;
      const color = shade === 'yellow' ? YELLOW_COLOR : MAGENTA_COLOR;
      drawTaperedStroke(ctx, pointsRef.current, color, strokeWidthRef.current);
    },
    [markerModeActive, previewShade, shadeAtCursor]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      const overlay = canvasRef.current;
      if (overlay) {
        try {
          overlay.releasePointerCapture(e.pointerId);
        } catch {
          // releasePointerCapture throws if the pointer wasn't captured.
          // Safe to swallow — we set capture on pointerdown.
        }
      }
      void composeAndUpload();
    },
    [composeAndUpload]
  );

  // Keep the canvas mounted whenever marker mode is active so the user's
  // freshly-drawn stroke stays visible from pointerup through the end of
  // the upload mutation. (composeAndUpload clears the canvas in its
  // `finally`, which runs after the API call resolves.) Concurrent-stroke
  // prevention happens via `pointer-events: none` on the canvas below
  // when `disabled` or `isUploading` is true — an in-flight stroke
  // continues to fire via pointer-capture, but new pointerdowns are
  // blocked. This avoids the React-fragility of unmounting the canvas
  // mid-upload (composeAndUpload's `finally` would have run cleanup
  // against a detached ref).
  const showOverlay = markerModeActive && renderedRect !== null;
  const interactionsBlocked = Boolean(disabled || isUploading);

  // Active = the preview color tints the icon. Idle = neutral gray
  // marker icon so the affordance reads as "marker tool" without
  // claiming a specific color before the user engages.
  const iconColor = markerModeActive
    ? previewShade === 'yellow'
      ? YELLOW_COLOR
      : MAGENTA_COLOR
    : undefined;

  // unavailableReason wins over the default labels because it's the
  // user-actionable explanation (cap reached, etc.). Falls through to
  // the regular labels when null.
  const buttonLabel = unavailableReason
    ? unavailableReason
    : markerModeActive
      ? 'Exit marker mode'
      : 'Use the marker to highlight the image and help our AI understand precisely what to do. Reference the name of your mark (provided under its thumbnail below) in your prompt.';

  // Split the "can't click" state in two so keyboard / screen-reader
  // users can discover WHY the cap is in effect:
  //   - hardDisabled (refining or uploading): the real `disabled`
  //     attribute. Button drops out of the tab order; the user can't
  //     interact mid-mutation anyway, so the loss of focusability is
  //     fine and visually matches the standard disabled treatment.
  //   - capDisabled (3-mark / 10-ref cap reached): `aria-disabled`
  //     only — and only when entry is what's being attempted. Exit
  //     stays operable even when capped (the click handler allows it),
  //     so a user already in marker mode reads as INTERACTIVE for
  //     screen readers + visuals. Button stays focusable so a keyboard
  //     user can land on it and have the screen reader announce the
  //     `aria-label` (which carries the cap reason on entry attempts).
  const hardDisabled = Boolean(disabled || isUploading);
  const capDisabled = Boolean(unavailableReason);
  // The button is inactive when entry is the only thing it could do
  // and entry is blocked. Active marker mode + cap = still interactive
  // (the user can exit), so `buttonInactive` flips to false there.
  const buttonInactive = hardDisabled || (capDisabled && !markerModeActive);

  return (
    <>
      {imageEl && (
        <div className="absolute top-2 right-3 z-20">
          <div className="tooltip-container">
            <button
              type="button"
              onClick={() => {
                // Cap-disabled blocks ENTRY only — the user must still
                // be able to exit marker mode if a cap landed while
                // they were already in it (e.g., another tab/device
                // added a mark and the session refresh raced the
                // local state). Without this branch the click would
                // be inert, leaving the drawing canvas mounted and
                // intercepting pointer events with no recourse beyond
                // Escape. `disabled` can't be used for the cap state
                // (would defocus the button and hide the reason from
                // screen readers), so the runtime guard here has to
                // do the work.
                if (capDisabled && !markerModeActive) return;
                if (markerModeActive) {
                  // Exit cleanly — drop any in-progress state.
                  isDrawingRef.current = false;
                  strokeBoundsRef.current = null;
                  pointsRef.current = [];
                  lockedShadeRef.current = null;
                  setMarkerModeActive(false);
                } else {
                  setMarkerModeActive(true);
                }
              }}
              disabled={hardDisabled}
              aria-disabled={buttonInactive}
              aria-label={buttonLabel}
              aria-pressed={markerModeActive}
              className={`inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#838a98]/90 backdrop-blur shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                // Re-apply the disabled visual treatment for the cap
                // case since we're not using the real `disabled`
                // attribute (so the `disabled:` pseudo-class doesn't
                // fire). hardDisabled already gets it via the
                // pseudo-class above. Skip the cap visual when
                // markerModeActive: exit is still operable in that
                // state so the button should look interactive.
                buttonInactive && !hardDisabled ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                markerModeActive
                  ? 'ring-2 ring-white/70'
                  : 'ring-1 ring-white/25 hover:ring-white/50'
              }`}
            >
              <span
                className="inline-flex h-5 w-5"
                // Mid-gray background gives both yellow and magenta clean
                // contrast in active mode; the idle icon goes near-white
                // so it stays visible against gray-400.
                style={{ color: iconColor ?? '#f9fafb' }}
              >
                <HighlighterIcon className="h-5 w-5" />
              </span>
            </button>
            {/* Visual-only hover tooltip — aria-hidden so screen readers
                don't announce the same text twice (the button's aria-label
                already carries it). Removing this element would lose the
                CSS hover affordance for sighted users; aria-hidden lets
                us keep the visual without the SR duplication. */}
            <span
              aria-hidden="true"
              className="tooltip-text tooltip-text-below tooltip-text-right"
              // width: max-content lets the bubble shrink to short labels
              // ("Exit marker mode") while the maxWidth still wraps the
              // long idle hint at a readable line length.
              style={{ whiteSpace: 'normal', width: 'max-content', maxWidth: 'min(20rem, 60vw)', textAlign: 'left' }}
            >
              {buttonLabel}
            </span>
          </div>
          {isUploading && (
            <span className="absolute -bottom-1 -right-1 inline-block h-3 w-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin bg-[#838a98]" />
          )}
        </div>
      )}

      {/* Drawing canvas — only mounted while marker mode is active so it
          doesn't intercept clicks on the underlying image when idle.

          Use the SAME rounded integer width/height for both the canvas's
          internal drawing buffer (`width`/`height` attrs) and its CSS box
          (`style.width`/`style.height`). Mixing rounded buffer dims with
          fractional CSS dims would mean pointer coords (CSS pixels via
          getBoundingClientRect) don't map 1:1 to the buffer, producing a
          sub-pixel stroke offset and slightly off bbox math under
          responsive layouts / zoom. */}
      {showOverlay && renderedRect && (() => {
        const cssWidth = Math.max(1, Math.round(renderedRect.width));
        const cssHeight = Math.max(1, Math.round(renderedRect.height));
        return (
          <canvas
            ref={canvasRef}
            width={cssWidth}
            height={cssHeight}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className="absolute z-10 cursor-crosshair touch-none"
            style={{
              left: renderedRect.left,
              top: renderedRect.top,
              width: cssWidth,
              height: cssHeight,
              // `pointer-events: none` blocks new pointerdowns during
              // upload/refinement while keeping the stroke visible.
              // In-flight strokes still complete because pointer-capture
              // bypasses this CSS rule.
              pointerEvents: interactionsBlocked ? 'none' : 'auto',
            }}
            aria-label="Draw a reference mark on the image"
          />
        );
      })()}
    </>
  );
}
